"use client"

// useBundlePerformance — hook for the server-discovered Bundle Performance
// endpoint. Server picks bundle types from shopify_order_bundle_groups;
// caller passes integration + period + free-shipping threshold.
//
// Client-side caching: module-level ResponseCache with 5-minute TTL keeps
// repeat clicks (period switch, tab return) instant while still expiring on
// long idle. Manual `refresh()` invalidates the relevant key before refetching
// — used by drill-down save to ensure fresh metadata-driven baseline metrics.

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type {
  BundlePerformanceResponse,
  BundleTypePerformance,
} from "@/lib/shopify/bundles/types"
import { ResponseCache, useResponseCacheGeneration } from "./_response-cache"

export interface UseBundlePerformanceOptions {
  integrationId: string
  /** Inclusive ISO-8601. */
  periodStart: string
  /** Exclusive ISO-8601. */
  periodEnd: string
  minimumOrderTotal?: number
  freeShippingThreshold?: number
  /**
   * User's IANA timezone (resolved at the page level via useAuth().user.timezone
   * with "UTC" fallback). Forwarded to the server so starts_at calendar
   * literals interpret as the user's calendar, not UTC.
   */
  timezone?: string
  /**
   * When true, the response also carries `hypotheticalTypes[]` — rows for
   * hypothetical bundle definitions, computed in isolation against the full
   * line items (no shared consumption pool with reals, so `bundleTypes` and
   * `totals` are byte-identical regardless of this flag — the load-bearing
   * invariant from Phase 4.4). Default false: the actuals payload stays slim
   * and identical to the legacy contract.
   */
  includeHypothetical?: boolean
}

export interface UseBundlePerformanceResult {
  bundleTypes: BundleTypePerformance[]
  /**
   * Always defined for caller convenience — `[]` when `includeHypothetical`
   * is false OR the response omits the field. Never overlaps with
   * `bundleTypes` (every row has `is_hypothetical: true`).
   */
  hypotheticalTypes: BundleTypePerformance[]
  totals: BundlePerformanceResponse["totals"] | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new ResponseCache<BundlePerformanceResponse>(CACHE_TTL_MS)

/**
 * Cache key shape: auth scope (user + active tenant) FIRST, then request
 * params. Including auth scope prevents a stale response from being served
 * across login/logout boundaries OR tenant-impersonation switches in the
 * same SPA session — without this, a super_user toggling impersonation
 * between two tenants could see the previous tenant's data on cache hit.
 *
 * activeTenantId resolves impersonation: super_user impersonating tenant X
 * has user.tenant_id = their home tenant but user.impersonated_tenant_id = X;
 * the cache key tracks the EFFECTIVE tenant scope so impersonation flips
 * always miss and refetch.
 */
function cacheKey(opts: {
  userId: string
  activeTenantId: string
  integrationId: string
  periodStart: string
  periodEnd: string
  minimumOrderTotal: number
  freeShippingThreshold: number
  timezone: string
  includeHypothetical: boolean
}): string {
  return [
    opts.userId,
    opts.activeTenantId,
    opts.integrationId,
    opts.periodStart,
    opts.periodEnd,
    opts.minimumOrderTotal,
    opts.freeShippingThreshold,
    opts.timezone,
    opts.includeHypothetical ? "h1" : "h0",
  ].join("|")
}

export function useBundlePerformance({
  integrationId,
  periodStart,
  periodEnd,
  minimumOrderTotal = 0,
  freeShippingThreshold = 0,
  timezone,
  includeHypothetical = false,
}: UseBundlePerformanceOptions): UseBundlePerformanceResult {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const cacheGeneration = useResponseCacheGeneration()
  const userId = user?.user_id ?? ""
  const activeTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? ""
  const [bundleTypes, setBundleTypes] = useState<BundleTypePerformance[]>([])
  const [hypotheticalTypes, setHypotheticalTypes] = useState<BundleTypePerformance[]>([])
  const [totals, setTotals] = useState<BundlePerformanceResponse["totals"] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stale-response guard. Increment on EVERY fetchData call (including cache
  // hits and invalid-input paths) so a slow in-flight network response can't
  // overwrite UI state set by a later request. Incrementing only on network
  // would miss the case "request A fires network → request B hits cache →
  // request A resolves and overwrites cached B's state".
  const requestSeqRef = useRef(0)

  // Drop the entire module cache on logout — defense in depth. Even though
  // every key includes user_id + activeTenantId so cross-user collisions
  // can't happen on lookup, dropping on logout prevents a memory residue of
  // protected payloads after the user signs out of the app.
  useEffect(() => {
    if (!isAuthenticated) cache.clear()
  }, [isAuthenticated])

  const fetchData = useCallback(
    async (useCache: boolean) => {
      const localSeq = ++requestSeqRef.current

      // When required inputs are missing, clear any stale data and bail out.
      // The previous early-return left the last successful response visible
      // after props became invalid (e.g. integration unset) — confusing UX.
      if (
        !integrationId ||
        !isAuthenticated ||
        !periodStart ||
        !periodEnd ||
        !userId ||
        !activeTenantId
      ) {
        if (localSeq === requestSeqRef.current) {
          setBundleTypes([])
          setHypotheticalTypes([])
          setTotals(null)
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const key = cacheKey({
        userId,
        activeTenantId,
        integrationId,
        periodStart,
        periodEnd,
        minimumOrderTotal,
        freeShippingThreshold,
        timezone: timezone ?? "UTC",
        includeHypothetical,
      })

      if (useCache) {
        const cached = cache.get(key)
        if (cached != null) {
          // Synchronous cache-hit — no isLoading flicker. State is set in one
          // batch so the component re-renders directly with the cached payload.
          if (localSeq === requestSeqRef.current) {
            setBundleTypes(cached.bundle_types)
            setHypotheticalTypes(cached.hypothetical_types ?? [])
            setTotals(cached.totals)
            setError(null)
            setIsLoading(false)
          }
          return
        }
      }

      setIsLoading(true)
      setError(null)

      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          period_start: periodStart,
          period_end: periodEnd,
          minimum_order_total: String(minimumOrderTotal),
          free_shipping_threshold: String(freeShippingThreshold),
        })
        if (timezone) params.set("timezone", timezone)
        if (includeHypothetical) params.set("include_hypothetical", "true")

        const res = await fetchWithAuth(`/api/integrations/shopify/bundle-performance?${params}`)
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }

        const data: BundlePerformanceResponse = await res.json()
        // cache.set is intentionally NOT seq-guarded: the payload is valid for
        // the (now-stale) request key, and future requests with the same key
        // benefit from the populated entry. Only setState is guarded.
        cache.set(key, data, cacheGeneration)
        if (localSeq === requestSeqRef.current) {
          setBundleTypes(data.bundle_types)
          setHypotheticalTypes(data.hypothetical_types ?? [])
          setTotals(data.totals)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load bundle performance"
          setError(message)
          setBundleTypes([])
          setHypotheticalTypes([])
          setTotals(null)
        }
        // Don't cache errors — next attempt should hit the network again.
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      cacheGeneration,
      integrationId,
      isAuthenticated,
      userId,
      activeTenantId,
      periodStart,
      periodEnd,
      minimumOrderTotal,
      freeShippingThreshold,
      timezone,
      includeHypothetical,
      fetchWithAuth,
    ],
  )

  // Public refresh: invalidates the current cache key before refetching, so
  // callers (e.g. drill-down save) always get server-fresh data.
  const refresh = useCallback(async () => {
    if (integrationId && periodStart && periodEnd && userId && activeTenantId) {
      cache.delete(
        cacheKey({
          userId,
          activeTenantId,
          integrationId,
          periodStart,
          periodEnd,
          minimumOrderTotal,
          freeShippingThreshold,
          timezone: timezone ?? "UTC",
          includeHypothetical,
        }),
      )
    }
    await fetchData(false)
  }, [
    fetchData,
    userId,
    activeTenantId,
    integrationId,
    periodStart,
    periodEnd,
    minimumOrderTotal,
    freeShippingThreshold,
    timezone,
    includeHypothetical,
  ])

  useEffect(() => {
    void fetchData(true)
  }, [fetchData])

  return { bundleTypes, hypotheticalTypes, totals, isLoading, error, refresh }
}
